import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, TruckIcon, Package, MapPin, Calendar, User, Phone, Mail } from 'lucide-react';
interface OrderData {
  orderNumber: string;
  totalPrice: number;
  name: string;
  phone: string;
  email: string;
  pickupAddress: string;
  pickupCity: string;
  deliveryAddress: string;
  deliveryCity: string;
  moveDate: string;
  status: string;
  paymentMethod?: string;
}
const Tracking = () => {
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  // For demo purposes, allow changing the status
  const [currentStatus, setCurrentStatus] = useState<string>('');
  useEffect(() => {
    // Get order data from localStorage
    const storedOrder = localStorage.getItem('moveOrder');
    if (storedOrder) {
      const parsedOrder = JSON.parse(storedOrder);
      setOrderData(parsedOrder);
      setCurrentStatus(parsedOrder.status || 'Scheduled');
    } else {
      // Redirect to order form if no order data exists
      navigate('/order');
    }
  }, [navigate]);
  const updateStatus = (newStatus: string) => {
    if (orderData) {
      const updatedOrder = {
        ...orderData,
        status: newStatus
      };
      setCurrentStatus(newStatus);
      localStorage.setItem('moveOrder', JSON.stringify(updatedOrder));
    }
  };
  const getStatusStep = (status: string) => {
    switch (status) {
      case 'Scheduled':
        return 1;
      case 'In Progress':
        return 2;
      case 'Delivered':
        return 3;
      default:
        return 1;
    }
  };
  if (!orderData) {
    return <div className="min-h-screen bg-gray-50 py-10" data-id="element-357">
        <div className="container mx-auto px-4" data-id="element-358">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm p-6 text-center" data-id="element-359">
            <p data-id="element-360">Loading order details...</p>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 py-10" data-id="element-361">
      <div className="container mx-auto px-4" data-id="element-362">
        <div className="max-w-3xl mx-auto" data-id="element-363">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-364">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8" data-id="element-365">
              <div data-id="element-366">
                <h1 className="text-2xl font-bold" data-id="element-367">Order Tracking</h1>
                <p className="text-gray-600 mt-1" data-id="element-368">
                  Order Number:{' '}
                  <span className="font-semibold" data-id="element-369">{orderData.orderNumber}</span>
                </p>
              </div>
              <div className="mt-4 md:mt-0" data-id="element-370">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${currentStatus === 'Delivered' ? 'bg-green-100 text-green-800' : currentStatus === 'In Progress' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-371">
                  {currentStatus === 'Delivered' && <CheckCircle className="h-4 w-4 mr-1" data-id="element-372" />}
                  {currentStatus === 'In Progress' && <TruckIcon className="h-4 w-4 mr-1" data-id="element-373" />}
                  {currentStatus === 'Scheduled' && <Clock className="h-4 w-4 mr-1" data-id="element-374" />}
                  {currentStatus}
                </span>
              </div>
            </div>
            {/* Status Timeline */}
            <div className="mb-8" data-id="element-375">
              <div className="relative" data-id="element-376">
                <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-377">
                  <div className="w-full border-t border-gray-300" data-id="element-378"></div>
                </div>
                <div className="relative flex justify-between" data-id="element-379">
                  <div className="flex flex-col items-center" data-id="element-380">
                    <div className={`h-10 w-10 flex items-center justify-center rounded-full ${getStatusStep(currentStatus) >= 1 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-381">
                      <Clock className={`h-5 w-5 ${getStatusStep(currentStatus) >= 1 ? 'text-white' : 'text-gray-500'}`} data-id="element-382" />
                    </div>
                    <p className={`mt-2 text-sm ${getStatusStep(currentStatus) >= 1 ? 'text-blue-600 font-medium' : 'text-gray-500'}`} data-id="element-383">
                      Scheduled
                    </p>
                  </div>
                  <div className="flex flex-col items-center" data-id="element-384">
                    <div className={`h-10 w-10 flex items-center justify-center rounded-full ${getStatusStep(currentStatus) >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-385">
                      <TruckIcon className={`h-5 w-5 ${getStatusStep(currentStatus) >= 2 ? 'text-white' : 'text-gray-500'}`} data-id="element-386" />
                    </div>
                    <p className={`mt-2 text-sm ${getStatusStep(currentStatus) >= 2 ? 'text-blue-600 font-medium' : 'text-gray-500'}`} data-id="element-387">
                      In Progress
                    </p>
                  </div>
                  <div className="flex flex-col items-center" data-id="element-388">
                    <div className={`h-10 w-10 flex items-center justify-center rounded-full ${getStatusStep(currentStatus) >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-389">
                      <CheckCircle className={`h-5 w-5 ${getStatusStep(currentStatus) >= 3 ? 'text-white' : 'text-gray-500'}`} data-id="element-390" />
                    </div>
                    <p className={`mt-2 text-sm ${getStatusStep(currentStatus) >= 3 ? 'text-blue-600 font-medium' : 'text-gray-500'}`} data-id="element-391">
                      Delivered
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-392">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-393">
                <h3 className="font-semibold mb-3 flex items-center" data-id="element-394">
                  <MapPin className="h-5 w-5 text-blue-600 mr-2" data-id="element-395" />
                  Move Details
                </h3>
                <div className="space-y-2" data-id="element-396">
                  <div data-id="element-397">
                    <p className="text-sm text-gray-500" data-id="element-398">From</p>
                    <p data-id="element-399">
                      {orderData.pickupAddress}, {orderData.pickupCity}
                    </p>
                  </div>
                  <div data-id="element-400">
                    <p className="text-sm text-gray-500" data-id="element-401">To</p>
                    <p data-id="element-402">
                      {orderData.deliveryAddress}, {orderData.deliveryCity}
                    </p>
                  </div>
                  <div data-id="element-403">
                    <p className="text-sm text-gray-500" data-id="element-404">Moving Date</p>
                    <p data-id="element-405">{new Date(orderData.moveDate).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-406">
                <h3 className="font-semibold mb-3 flex items-center" data-id="element-407">
                  <User className="h-5 w-5 text-blue-600 mr-2" data-id="element-408" />
                  Contact Information
                </h3>
                <div className="space-y-2" data-id="element-409">
                  <div data-id="element-410">
                    <p className="text-sm text-gray-500" data-id="element-411">Name</p>
                    <p data-id="element-412">{orderData.name}</p>
                  </div>
                  <div data-id="element-413">
                    <p className="text-sm text-gray-500" data-id="element-414">Phone</p>
                    <p data-id="element-415">{orderData.phone}</p>
                  </div>
                  <div data-id="element-416">
                    <p className="text-sm text-gray-500" data-id="element-417">Email</p>
                    <p data-id="element-418">{orderData.email}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-6" data-id="element-419">
              <div className="flex justify-between items-center mb-6" data-id="element-420">
                <span className="text-lg font-semibold" data-id="element-421">Total Amount:</span>
                <div data-id="element-422">
                  <span className="text-2xl font-bold" data-id="element-423">
                    €{orderData.totalPrice}
                  </span>
                  {orderData.paymentMethod && <p className="text-sm text-gray-500 text-right" data-id="element-424">
                      Paid via{' '}
                      {orderData.paymentMethod === 'card' ? 'Credit Card' : orderData.paymentMethod === 'bank' ? 'Bank Link' : 'Cash on Delivery'}
                    </p>}
                </div>
              </div>
              {/* Demo Controls - In a real app, these would be removed */}
              <div className="bg-gray-100 p-4 rounded-lg mb-6" data-id="element-425">
                <p className="text-sm font-medium mb-2" data-id="element-426">
                  Demo Controls - Update Status:
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-427">
                  <button onClick={() => updateStatus('Scheduled')} className={`px-3 py-1 text-sm rounded ${currentStatus === 'Scheduled' ? 'bg-yellow-200 text-yellow-800' : 'bg-gray-200 text-gray-700'}`} data-id="element-428">
                    Scheduled
                  </button>
                  <button onClick={() => updateStatus('In Progress')} className={`px-3 py-1 text-sm rounded ${currentStatus === 'In Progress' ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-700'}`} data-id="element-429">
                    In Progress
                  </button>
                  <button onClick={() => updateStatus('Delivered')} className={`px-3 py-1 text-sm rounded ${currentStatus === 'Delivered' ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-700'}`} data-id="element-430">
                    Delivered
                  </button>
                </div>
              </div>
              <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-431">
                <Link to="/" className="text-blue-600 mb-4 md:mb-0 hover:underline" data-id="element-432">
                  Return to Homepage
                </Link>
                <a href={`mailto:support@estmove.ee?subject=Support for Order ${orderData.orderNumber}`} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center" data-id="element-433">
                  <Mail className="mr-2 h-5 w-5" data-id="element-434" />
                  Contact Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Tracking;